import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { searchFacilitiesByAddress, logActivity, GeocodingResult, SearchResponse, SearchResult, ReleaseInfo } from '../services/api';
import MapboxAutocomplete from './MapboxAutocomplete';
import FacilityDetails from './FacilityDetails';
import ReleaseDetails from './ReleaseDetails';


interface MapboxFeature {
  id: string;
  place_name: string;
  center: [number, number];
  geometry: {
    coordinates: [number, number];
  };
  properties: {
    address?: string;
  };
}

const SearchPage: React.FC = () => {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const [searchAddress, setSearchAddress] = useState<string>('2101 South Holly Street, Denver, Colorado 80222, United States');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [geocodingResult, setGeocodingResult] = useState<GeocodingResult | null>(null);
  const [totalFound, setTotalFound] = useState<number>(0);
  const [selectedCoordinates, setSelectedCoordinates] = useState<{lat: number, lon: number} | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<SearchResult | null>(null);
  const [closestRelease, setClosestRelease] = useState<ReleaseInfo | null>(null);
  const [selectedRelease, setSelectedRelease] = useState<{id: string, name: string} | null>(null);

  const handleAddressSelect = (feature: MapboxFeature) => {
    const [lon, lat] = feature.center;
    setSelectedCoordinates({ lat, lon });
    console.log('Address selected:', feature.place_name, 'Coordinates:', lat, lon);
  };

  const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchAddress(e.target.value);
    if (!e.target.value) {
      setSelectedCoordinates(null);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchAddress.trim()) {
      alert('Please enter an address to search');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const token = await getAccessTokenSilently();
      const response: SearchResponse = await searchFacilitiesByAddress(token, searchAddress);
      
      setSearchResults(response.facilities || []);
      setGeocodingResult(response.geocoding);
      setTotalFound(response.total_found || 0);
      setClosestRelease(response.closest_release || null);
      
      if (response.geocoding.success) {
        console.log('Geocoding successful:', response.geocoding);
      } else {
        console.warn('Geocoding failed:', response.geocoding.message);
      }
      
      if (response.closest_release) {
        console.log('Closest release found:', response.closest_release.lust_id, 'at', response.closest_release.distance_miles, 'miles');
      }
      
      logActivity('address_search', user);
    } catch (error) {
      console.error('Search error:', error);
      // Sample data for development
      setSearchResults([
        {
          id: '1',
          name: 'Downtown Gas Station',
          address: '100 Main Street',
          city: 'Sample City',
          state: 'NY',
          zip_code: '10001',
          latitude: 40.7128,
          longitude: -74.0060,
          tank_count: 4,
          has_leaks: false,
          distance_miles: 0.3,
          county_fips: '36001'
        },
        {
          id: '2',
          name: 'Highway Service Center',
          address: '250 Highway 95',
          city: 'Sample City',
          state: 'NY',
          zip_code: '10002',
          latitude: 40.7200,
          longitude: -74.0100,
          tank_count: 6,
          has_leaks: true,
          distance_miles: 1.2,
          county_fips: '36001'
        }
      ]);
    } finally {
      setIsSearching(false);
    }
  };


  if (!isAuthenticated) {
    return (
      <div className="container">
        <p>Please log in to search facilities.</p>
      </div>
    );
  }

  return (
    <div className="container">
      <h2>Search Storage Tank Facilities</h2>
      
      <div className="search-container">
        <form onSubmit={handleSearch}>
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="address" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Search Address:
            </label>
            <MapboxAutocomplete
              id="address"
              name="address"
              value={searchAddress}
              onChange={handleAddressInputChange}
              onAddressSelect={handleAddressSelect}
              placeholder="Enter street address, city, state, or ZIP code"
              className="search-input"
              required
            />
          </div>
          
          
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isSearching}
            style={{ fontSize: '1.1rem', padding: '0.75rem 2rem' }}
          >
            {isSearching ? 'Searching...' : 'Search Facilities'}
          </button>
        </form>
      </div>

      {closestRelease && (
        <div style={{ 
          backgroundColor: '#fef2f2',
          border: '2px solid #dc2626',
          borderRadius: '8px',
          padding: '1rem',
          margin: '1rem 0'
        }}>
          <h4 style={{ 
            margin: '0 0 0.5rem 0',
            color: '#dc2626'
          }}>
            ⚠️ Closest Known Release/Leak Location
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.5rem' }}>
            <p style={{ margin: '0.25rem 0' }}>
              <strong>Site:</strong> {closestRelease.name}
            </p>
            <p style={{ margin: '0.25rem 0' }}>
              <strong>Distance:</strong> {closestRelease.distance_miles} miles
            </p>
            <p style={{ margin: '0.25rem 0' }}>
              <strong>Status:</strong> 
              <span style={{ 
                marginLeft: '0.5rem',
                padding: '0.125rem 0.5rem',
                borderRadius: '4px',
                backgroundColor: closestRelease.status === 'Open' ? '#dc2626' : '#10b981',
                color: 'white',
                fontSize: '0.875rem'
              }}>
                {closestRelease.status}
              </span>
            </p>
            <p style={{ margin: '0.25rem 0' }}>
              <strong>Location:</strong> {closestRelease.city}, {closestRelease.state}
            </p>
            {closestRelease.substance && closestRelease.substance !== 'Unknown' && (
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Substance:</strong> {closestRelease.substance}
              </p>
            )}
            {closestRelease.population_within_1500ft > 0 && (
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Population within 1500ft:</strong> {closestRelease.population_within_1500ft}
              </p>
            )}
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => {
              logActivity('release_view', user, closestRelease.lust_id);
              setSelectedRelease({ id: closestRelease.lust_id, name: closestRelease.name });
            }}
            style={{ marginTop: '0.75rem' }}
          >
            View Release Details
          </button>
        </div>
      )}

      {geocodingResult && (
        <div style={{ 
          backgroundColor: geocodingResult.success ? '#f0f9ff' : '#fef2f2',
          border: `1px solid ${geocodingResult.success ? '#0ea5e9' : '#ef4444'}`,
          borderRadius: '8px',
          padding: '1rem',
          margin: '1rem 0'
        }}>
          <h4 style={{ 
            margin: '0 0 0.5rem 0',
            color: geocodingResult.success ? '#0ea5e9' : '#ef4444'
          }}>
            Address Geocoding
          </h4>
          {geocodingResult.success ? (
            <div>
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Original Address:</strong> {geocodingResult.original_address}
              </p>
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Formatted Address:</strong> {geocodingResult.formatted_address}
              </p>
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Coordinates:</strong> {geocodingResult.latitude?.toFixed(6)}, {geocodingResult.longitude?.toFixed(6)}
              </p>
            </div>
          ) : (
            <div>
              <p style={{ margin: '0.25rem 0', color: '#ef4444' }}>
                <strong>Error:</strong> {geocodingResult.message}
              </p>
              <p style={{ margin: '0.25rem 0', fontSize: '0.9rem', color: '#6b7280' }}>
                Address: {geocodingResult.original_address || searchAddress}
              </p>
            </div>
          )}
        </div>
      )}

      {hasSearched && !isSearching && (
        <div>
          <h3>Search Results</h3>
          
          {searchResults.length === 0 ? (
            <div className="search-container">
              <p>No facilities found near "{searchAddress}".</p>
              <p>Try searching a different location.</p>
            </div>
          ) : (
            <>
              <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                Found {searchResults.length} closest facilities to "{searchAddress}"
                {searchResults.length > 0 && (
                  <span> (closest: {searchResults[0].distance_miles} miles, furthest: {searchResults[searchResults.length - 1].distance_miles} miles)</span>
                )}
              </p>

              
              <div className="search-results">
                {searchResults.map((facility) => (
                  <div key={facility.id} className="facility-card">
                    {/* Pricing Estimate Header */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                      gap: '1rem',
                      marginBottom: '0.5rem'
                    }}>
                      <div></div> {/* Empty space above facility details */}
                      <div style={{
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: '#475569',
                        gridColumn: 'span 4'
                      }}>
                        Pricing Estimate
                      </div>
                    </div>

                    {/* 1 Row x 5 Columns Layout */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                      gap: '1rem',
                      alignItems: 'center',
                      minHeight: '80px',
                      marginBottom: '1rem'
                    }}>

                      {/* Column 1: Facility Details */}
                      <div>
                        <h4 style={{ margin: '0 0 0.25rem 0', color: '#1f2937', fontSize: '1.1rem' }}>
                          {facility.name}
                        </h4>
                        <p style={{ margin: '0.25rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
                          {facility.address}, {facility.city}, {facility.state} {facility.zip_code}
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem', fontSize: '0.8rem', color: '#4b5563' }}>
                          <span><strong>Distance:</strong> {facility.distance_miles} miles</span>
                          <span><strong>Tanks:</strong> {facility.tank_count}</span>
                          <span><strong>Leaks:</strong> {facility.has_leaks ? 'Yes' : 'No'}</span>
                        </div>
                      </div>

                      {/* Pricing Estimate Header (spans all 4 pricing columns) */}
                      {facility.pricing && facility.pricing.quotes && facility.pricing.quotes.length > 0 ? (
                        <>
                          {facility.pricing.quotes.slice(0, 4).map((quote: any, index: number) => {
                            const isLowest = facility.pricing?.summary &&
                              quote.annual_premium === facility.pricing.summary.lowest_premium;
                            return (
                              <div
                                key={index}
                                style={{
                                  textAlign: 'center',
                                  padding: '0.75rem 0.5rem',
                                  backgroundColor: isLowest ? '#f0fdf4' : '#f8fafc',
                                  border: isLowest ? '2px solid #10b981' : '1px solid #e2e8f0',
                                  borderRadius: '6px'
                                }}
                              >
                                <div style={{
                                  fontSize: '0.7rem',
                                  fontWeight: '600',
                                  color: '#475569',
                                  marginBottom: '0.25rem'
                                }}>
                                  {quote.carrier}
                                </div>
                                <div style={{
                                  fontSize: '0.85rem',
                                  fontWeight: '700',
                                  color: isLowest ? '#059669' : '#1f2937'
                                }}>
                                  ${quote.annual_premium.toLocaleString()}
                                </div>
                                {isLowest && (
                                  <div style={{
                                    fontSize: '0.6rem',
                                    color: '#059669',
                                    fontWeight: '600',
                                    marginTop: '0.1rem'
                                  }}>
                                    BEST
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <>
                          {/* Show 4 empty pricing columns when no pricing */}
                          {[1, 2, 3, 4].map((col) => (
                            <div
                              key={col}
                              style={{
                                textAlign: 'center',
                                padding: '0.75rem 0.5rem',
                                backgroundColor: '#fef3c7',
                                border: '1px solid #fbbf24',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                color: '#92400e'
                              }}
                            >
                              Quote {col}
                              <br />
                              <span style={{ fontSize: '0.65rem' }}>Available</span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button 
                        className="btn btn-primary"
                        onClick={() => {
                          logActivity('facility_view', user, facility.id);
                          setSelectedFacility(facility);
                        }}
                      >
                        View Details
                      </button>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => {
                          window.open(`https://maps.google.com?q=${facility.latitude},${facility.longitude}`, '_blank');
                        }}
                      >
                        View on Map
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {selectedFacility && (
        <FacilityDetails
          facilityId={selectedFacility.id}
          facilityName={selectedFacility.name}
          onClose={() => setSelectedFacility(null)}
          pricingInfo={selectedFacility.pricing ? {
            facility_id: selectedFacility.id,
            tank_count: selectedFacility.tank_count || 0,
            risk_score: (selectedFacility as any).risk_score || 0,
            quotes: selectedFacility.pricing.quotes || [],
            summary: selectedFacility.pricing.summary || {
              best_carrier: '',
              lowest_premium: 0,
              savings_vs_highest: 0,
              average_premium: 0
            }
          } : undefined}
        />
      )}

      {selectedRelease && (
        <ReleaseDetails
          lustId={selectedRelease.id}
          releaseName={selectedRelease.name}
          onClose={() => setSelectedRelease(null)}
        />
      )}
    </div>
  );
};

export default SearchPage;